import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import LoadingIcon from "../../images/Loading_icon.gif";
import "../../styles/AISearchModal.css";
import { UrlContext } from "../../contexts/UrlContext";
import Swal from "sweetalert2";

// CONSULTING_FIRMS is now fetched dynamically from the backend

export default function AISearchModal({ isOpen, onClose, results, isLoading, tableData, setDoRefresh, projectId, setAiSearchResults }) {
  const { API_URL } = useContext(UrlContext);
  const [localAddedExperts, setLocalAddedExperts] = useState({});
  const [loadingAction, setLoadingAction] = useState({});
  const [summary, setSummary] = useState(null);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [isSearchTriggering, setIsSearchTriggering] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [hasSearchedEmpty, setHasSearchedEmpty] = useState(false);

  const [availableConsultingFirms, setAvailableConsultingFirms] = useState([]);
  const [newFirmName, setNewFirmName] = useState("");
  const [isAddingFirm, setIsAddingFirm] = useState(false);

  const [chatHistory, setChatHistory] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [visibleExperts, setVisibleExperts] = useState([]);
  const [chatInput, setChatInput] = useState("");

  useEffect(() => {
    setVisibleExperts(results || []);
    setChatHistory([]);
  }, [results]);

  useEffect(() => {
    if (isOpen) {
      fetchConsultingFirms();
    }
  }, [isOpen]);

  const fetchConsultingFirms = async () => {
    try {
      const response = await fetch(`${API_URL}get-consulting-firms`);
      const data = await response.json();
      if (data.status === "success" && data.consulting_firms) {
        setAvailableConsultingFirms(data.consulting_firms);
      }
    } catch (error) {
      console.error("Failed to fetch consulting firms:", error);
      // Fallback
      setAvailableConsultingFirms([
        "EY", "Accenture", "PwC", "Deloitte", "McKinsey & Company",
        "KPMG", "Boston Consulting Group (BCG)", "Capgemini", "Roland Berger"
      ]);
    }
  };

  const prepareExpertsForChat = (allExperts) => {
    return (allExperts || []).map(exp => ({
      id: exp.id,
      position: exp.position,
      skills: exp.skills,
      experience: exp.experience
    }));
  };

  const handleSendChatMessage = async (e) => {
    if (e) e.preventDefault();
    if (!chatInput.trim() || isChatLoading) return;

    const userMsg = chatInput.trim();
    setChatInput("");
    setChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsChatLoading(true);

    try {
      const response = await fetch('https://ai.expertpowerhouse.com/expert-search/chat-filter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg,
          experts: prepareExpertsForChat(results)
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        setChatHistory(prev => [...prev, { role: 'ai', content: data.message }]);
        const newOrderIds = data.expert_ids || [];
        const filteredAndOrderedExperts = newOrderIds.map(id => {
          return results.find(exp => exp.id === id);
        }).filter(Boolean);
        setVisibleExperts(filteredAndOrderedExperts);
      } else {
        throw new Error(data.message || "Failed to process chat");
      }
    } catch (error) {
      console.error("Chat error:", error);
      setChatHistory(prev => [...prev, { role: 'error', content: "There was an error processing your request." }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleClearChat = () => {
    setChatHistory([]);
    setVisibleExperts(results || []);
    setChatInput("");
  };

  const handleAddFirm = async () => {
    if (!newFirmName.trim()) return;
    setIsAddingFirm(true);
    try {
      const response = await fetch(`${API_URL}add-consulting-firm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newFirmName.trim() }),
      });
      const data = await response.json();
      if (data.status === "success") {
        const firm = data.firm || newFirmName.trim();
        setAvailableConsultingFirms(prev => {
          if (!prev.includes(firm)) return [...prev, firm];
          return prev;
        });
        // Select it
        setSummary(prev => {
          if (!prev) return prev;
          const current = prev.consulting_firms || [];
          if (!current.includes(firm)) {
            return { ...prev, consulting_firms: [...current, firm] };
          }
          return prev;
        });
        setNewFirmName("");
        Swal.fire({
          icon: "success",
          title: "Added",
          text: "Consulting firm added successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
         throw new Error(data.message || "Failed to add firm");
      }
    } catch (error) {
      console.error("Failed to add consulting firm:", error);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to add firm" });
    } finally {
      setIsAddingFirm(false);
    }
  };

  useEffect(() => {
    if (isOpen && tableData) {
      const initialMap = {};
      tableData.forEach(item => {
        const id = item.expert?.ID || item.expert?.id || item.expert_id || item.id;
        if (id) initialMap[Number(id)] = true;
      });
      setLocalAddedExperts(initialMap);
    }
  }, [isOpen, tableData]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchSummary();
    }
  }, [isOpen, projectId]);

  const fetchSummary = async () => {
    setIsSummaryLoading(true);
    try {
      const response = await fetch("https://ai.expertpowerhouse.com/expert-search/get-project-summary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectID: Number(projectId) }),
      });
      const data = await response.json();
      if (data.status === "success" && data.summary) {
        const proj = data.summary;
        
        const ensureArray = (val) => {
          if (val === undefined || val === null) return [];
          if (Array.isArray(val)) return val;
          if (typeof val === "string") return val.split(",").map((i) => i.trim()).filter(Boolean);
          if (typeof val === "boolean") return val ? ["Yes"] : ["No"];
          return [String(val)];
        };

        setSummary({
          country: proj.country || "",
          type: proj.type || "",
          engagement_type: proj.engagement_type || "",
          seniority_score: proj.seniority_score || null,
          confidence: proj.confidence || null,
          requires_tier1_consulting: proj.requires_tier1_consulting ?? false,
          remote: proj.remote ?? false,
          keywords: ensureArray(proj.keywords),
          languages: ensureArray(proj.languages),
          musthave: Array.isArray(proj.musthave) ? proj.musthave.join(" AND ") : (proj.musthave || ""),
          nice_to_have: ensureArray(proj.nice_to_have),
          project_id: proj.project_id,
          consulting_firms: ensureArray(proj.consulting_firms),
          is_consulting: (proj.is_consulting ?? false) || (proj.requires_tier1_consulting ?? false) || (ensureArray(proj.consulting_firms).length > 0)
        });
      }
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const formatSummaryForAPI = (rawSummary) => {
    if (!rawSummary) return null;
    const formatted = {
      ...rawSummary,
      keywords: typeof rawSummary.keywords === "string" 
        ? rawSummary.keywords.split(",").map(i => i.trim()).filter(Boolean) 
        : rawSummary.keywords,
      languages: typeof rawSummary.languages === "string" 
        ? rawSummary.languages.split(",").map(i => i.trim()).filter(Boolean) 
        : rawSummary.languages,
      musthave: rawSummary.musthave,
      nice_to_have: typeof rawSummary.nice_to_have === "string"
        ? rawSummary.nice_to_have.split(",").map(i => i.trim()).filter(Boolean)
        : (rawSummary.nice_to_have || []),
      consulting_firms: rawSummary.is_consulting ? (rawSummary.consulting_firms || []) : [],
    };
    return formatted;
  };

  const handleSaveSummary = async () => {
    setIsSavingSummary(true);
    const formatted = formatSummaryForAPI(summary);
    
    const payload = { ...formatted };
    delete payload.is_consulting;

    try {
      const response = await fetch("https://ai.expertpowerhouse.com/expert-search/edit-project-summary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectID: Number(projectId), summary: payload }),
      });
      const data = await response.json();
      if (data.status === "success") {
        setSummary(formatted); // Update local state with cleaned arrays
        setIsEditingSummary(false);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Summary updated successfully",
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error("Failed to update summary:", error);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to update summary" });
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handleTriggerSearch = async () => {
    setIsSearchTriggering(true);
    setHasSearchedEmpty(false);
    const formatted = formatSummaryForAPI(summary);

    const payload = { ...formatted };
    delete payload.is_consulting;

    try {
      const response = await fetch("https://ai.expertpowerhouse.com/expert-search/search-experts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      
      if (data.status === "error" && data.message === "No results") {
        setAiSearchResults(prev => ({
          ...prev,
          [projectId]: { experts: [], filters: {} }
        }));
        setHasSearchedEmpty(true);
        Swal.fire({
          icon: "info",
          title: "No results",
          text: "No experts were found matching these criteria.",
        });
      } else if (data.status === "success" && data.experts) {
        setAiSearchResults(prev => ({
          ...prev,
          [projectId]: { experts: data.experts, filters: data.applied_filters || {} }
        }));
        setSummary(formatted); // Ensure state is synced with what was searched
        setShowSummary(false); // Collapse the summary box
        Swal.fire({
            icon: "success",
            title: "Success",
            text: "Experts searched successfully",
            timer: 1500,
            showConfirmButton: false
          });
      } else {
        throw new Error(data.message || "Search failed");
      }
    } catch (error) {
      console.error("Search failed:", error);
      Swal.fire({ icon: "error", title: "Error", text: "Search failed" });
    } finally {
      setIsSearchTriggering(false);
    }
  };

  const handleSummaryChange = (field, value) => {
    setSummary(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, value) => {
    // Just store the string while editing to allow spaces/commas
    handleSummaryChange(field, value);
  };

  const toggleConsultingFirm = (firm) => {
    setSummary(prev => {
      const current = prev.consulting_firms || [];
      const next = current.includes(firm)
        ? current.filter(f => f !== firm)
        : [...current, firm];
      return { ...prev, consulting_firms: next };
    });
  };

  const handleSelectAllConsulting = () => {
    setSummary(prev => ({ ...prev, consulting_firms: [...availableConsultingFirms] }));
  };

  const handleDeselectAllConsulting = () => {
    setSummary(prev => ({ ...prev, consulting_firms: [] }));
  };

  const getScoreDetails = (expert) => {
    let score = null;
    if (expert.jev_score !== undefined && expert.jev_score !== null && !isNaN(Number(expert.jev_score))) {
      score = Number(expert.jev_score);
    } else if (expert.adaptation_percentage !== undefined && expert.adaptation_percentage !== null && !isNaN(Number(expert.adaptation_percentage))) {
      score = Number(expert.adaptation_percentage) / 10;
    } else if (expert.score !== undefined && expert.score !== null && !isNaN(Number(expert.score))) {
      const raw = Number(expert.score);
      score = raw <= 1 ? raw * 10 : raw;
    }

    if (score === null || isNaN(score)) {
      return {
        text: "SCORE: N/A",
        bg: "#f3f4f6",
        color: "#6b7280",
        border: "#e5e7eb"
      };
    }

    const clamped = Math.max(0, Math.min(10, score));
    const formatted = clamped.toFixed(1);

    if (clamped >= 7.0) {
      return {
        text: `SCORE: ${formatted}`,
        bg: "#dcfce7",
        color: "#15803d",
        border: "#86efac"
      };
    }
    if (clamped >= 5.0) {
      return {
        text: `SCORE: ${formatted}`,
        bg: "#e0f2fe",
        color: "#0369a1",
        border: "#7dd3fc"
      };
    }
    if (clamped >= 4.0) {
      return {
        text: `SCORE: ${formatted}`,
        bg: "#fef3c7",
        color: "#b45309",
        border: "#fde68a"
      };
    }
    return {
      text: `SCORE: ${formatted}`,
      bg: "#fee2e2",
      color: "#dc2626",
      border: "#fca5a5"
    };
  };

  if (!isOpen) return null;

  const handleSelect = (expertId) => {
    setLoadingAction(prev => ({ ...prev, [expertId]: true }));
    const ephToken = localStorage.getItem("eph-token");
    const token = JSON.parse(ephToken).value;
    const url = `${API_URL}select-expert/${projectId}/${expertId}`;
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": token,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setLocalAddedExperts(prev => ({ ...prev, [Number(expertId)]: true }));
        setLoadingAction(prev => ({ ...prev, [expertId]: false }));
        if (setDoRefresh) {
          setDoRefresh(prev => !prev);
        }
      })
      .catch((err) => {
        console.log(err);
        setLoadingAction(prev => ({ ...prev, [expertId]: false }));
      });
  };

  const handleUnselect = (expertId) => {
    setLoadingAction(prev => ({ ...prev, [expertId]: true }));
    const url = `${API_URL}unselect-expert/${projectId}/${expertId}`;
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setLocalAddedExperts(prev => ({ ...prev, [Number(expertId)]: false }));
        setLoadingAction(prev => ({ ...prev, [expertId]: false }));
        if (setDoRefresh) {
          setDoRefresh(prev => !prev);
        }
      })
      .catch((err) => {
        console.log(err);
        setLoadingAction(prev => ({ ...prev, [expertId]: false }));
      });
  };

  return (
    <div className="project-info-popup-wrapper" style={{ zIndex: 1000 }}>
      <div className="project-info-div" style={{ width: "95%", maxWidth: "1300px", height: "90vh", display: "flex", flexDirection: "column" }}>
        <i
          className="pjinfo-close-icon fa-regular fa-rectangle-xmark"
          onClick={onClose}
          style={{ top: "20px", right: "20px" }}
        ></i>
        
        <h4 className="text-muted" style={{ fontWeight: "bold", marginBottom: "20px" }}>
          <b>AI Expert Search</b>
        </h4>

        {/* Summary Section */}
        <div className="summary-section mb-3" style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "8px", border: "1px solid #dee2e6" }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0" style={{ cursor: "pointer", display: "flex", alignItems: "center" }} onClick={() => setShowSummary(!showSummary)}>
              <i className={`fa-solid ${showSummary ? "fa-chevron-down" : "fa-chevron-right"} mr-2`} style={{ fontSize: "0.8em" }}></i>
              Project Summary
            </h5>
            <div className="d-flex align-items-center">
              {showSummary && (
                <div className="mr-3">
                  {isEditingSummary ? (
                    <>
                      <button className="btn btn-sm btn-success mr-2" onClick={handleSaveSummary} disabled={isSavingSummary}>
                        {isSavingSummary ? "Saving..." : "Save Changes"}
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setIsEditingSummary(false)}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setIsEditingSummary(true)}>Edit Summary</button>
                  )}
                </div>
              )}
              {!showSummary && summary && (
                <button className="btn btn-sm btn-primary py-0 px-3" style={{ height: "30px" }} onClick={handleTriggerSearch} disabled={isSearchTriggering || isSummaryLoading || isEditingSummary}>
                    {isSearchTriggering ? "..." : "Search"}
                </button>
              )}
            </div>
          </div>

          {showSummary && (
            <div className="mt-3">
              {isSummaryLoading ? (
                <div className="text-center py-3">
                  <img src={LoadingIcon} alt="Loading..." style={{ height: "30px" }} />
                  <p className="mt-2 small">Loading summary...</p>
                </div>
              ) : summary ? (
                <>
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="small font-weight-bold">Country</label>
                      {isEditingSummary ? (
                        <input className="form-control form-control-sm" value={summary.country || ""} onChange={(e) => handleSummaryChange("country", e.target.value)} />
                      ) : (
                        <div className="p-2 bg-white border rounded small">{summary.country || "Not specified"}</div>
                      )}
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="small font-weight-bold">Type</label>
                      {isEditingSummary ? (
                        <input className="form-control form-control-sm" value={summary.type || ""} onChange={(e) => handleSummaryChange("type", e.target.value)} />
                      ) : (
                        <div className="p-2 bg-white border rounded small">{summary.type || "Not specified"}</div>
                      )}
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="small font-weight-bold">Remote</label>
                      {isEditingSummary ? (
                        <select className="form-control form-control-sm" value={summary.remote} onChange={(e) => handleSummaryChange("remote", e.target.value === "true")}>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <div className="p-2 bg-white border rounded small">{summary.remote ? "Yes" : "No"}</div>
                      )}
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="small font-weight-bold">Languages (comma separated)</label>
                      {isEditingSummary ? (
                        <textarea 
                          className="form-control form-control-sm" 
                          rows="1" 
                          value={Array.isArray(summary.languages) ? summary.languages.join(", ") : (summary.languages || "")} 
                          onChange={(e) => handleArrayChange("languages", e.target.value)} 
                        />
                      ) : (
                        <div className="p-2 bg-white border rounded small">{summary.languages?.join(", ") || "None"}</div>
                      )}
                    </div>

                    {(summary.seniority_score || summary.engagement_type) && (
                      <div className="col-12 mb-3">
                        <div className="p-2 border rounded bg-white d-flex flex-wrap align-items-center justify-content-between" style={{ gap: "10px" }}>
                          <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
                            {summary.engagement_type && (
                              <div>
                                <span className="text-muted mr-1 font-weight-bold small">Engagement Model:</span>
                                <span className="badge badge-info text-capitalize" style={{ fontSize: "12px", padding: "4px 8px" }}>
                                  {summary.engagement_type.replace(/_/g, " ")}
                                </span>
                              </div>
                            )}
                            {summary.seniority_score && (
                              <div>
                                <span className="text-muted mr-1 font-weight-bold small">Required Seniority:</span>
                                <span className="badge badge-dark" style={{ fontSize: "12px", padding: "4px 8px" }}>
                                  {summary.seniority_score} / 10
                                  {summary.confidence ? ` (${Math.round(summary.confidence * 100)}% confidence)` : ""}
                                </span>
                              </div>
                            )}
                            {summary.requires_tier1_consulting && (
                              <span className="badge badge-warning" style={{ fontSize: "12px", padding: "4px 8px" }}>
                                <i className="fa-solid fa-award mr-1"></i>Top-Tier Consulting Desired
                              </span>
                            )}
                          </div>
                          <span className="text-muted" style={{ fontSize: "11px" }}>
                            <i className="fa-solid fa-brain mr-1 text-primary"></i>Calibrated via TypeSafe Jev
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="col-12 mb-3">
                      <label className="small font-weight-bold">Must Have <span className="text-muted font-weight-normal">(separate items with AND or OR)</span></label>
                      {isEditingSummary ? (
                        <textarea
                          className="form-control form-control-sm"
                          rows="2"
                          placeholder="e.g. SAP AND retail background OR supply chain"
                          value={Array.isArray(summary.musthave) ? summary.musthave.join(" AND ") : (summary.musthave || "")}
                          onChange={(e) => handleSummaryChange("musthave", e.target.value)}
                        />
                      ) : (
                        <div className="p-2 bg-white border rounded small">
                          {Array.isArray(summary.musthave) ? summary.musthave.join(" AND ") : (summary.musthave || "None")}
                        </div>
                      )}
                    </div>

                    <div className="col-12 mb-3">
                      <label className="small font-weight-bold">
                        Nice to Have <span className="text-muted font-weight-normal">(validated by AI as non-blocking / desirable skills, comma separated)</span>
                      </label>
                      {isEditingSummary ? (
                        <textarea
                          className="form-control form-control-sm"
                          rows="2"
                          placeholder="e.g. Python Programming, Change Management"
                          value={Array.isArray(summary.nice_to_have) ? summary.nice_to_have.join(", ") : (summary.nice_to_have || "")}
                          onChange={(e) => handleArrayChange("nice_to_have", e.target.value)}
                        />
                      ) : (
                        <div className="p-2 bg-white border rounded small">
                          {Array.isArray(summary.nice_to_have) && summary.nice_to_have.length > 0
                            ? summary.nice_to_have.join(", ")
                            : (summary.nice_to_have || "None")}
                        </div>
                      )}
                    </div>

                    <div className="col-12 mb-3">
                      <label className="small font-weight-bold">Contextual AI search <span className="text-muted font-weight-normal">(comma separated)</span></label>
                      {isEditingSummary ? (
                        <textarea 
                          className="form-control form-control-sm" 
                          rows="2" 
                          value={Array.isArray(summary.keywords) ? summary.keywords.join(", ") : (summary.keywords || "")} 
                          onChange={(e) => handleArrayChange("keywords", e.target.value)} 
                        />
                      ) : (
                        <div className="p-2 bg-white border rounded small">{summary.keywords?.join(", ") || "None"}</div>
                      )}
                    </div>

                    <div className="col-12 mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <input 
                          type="checkbox" 
                          id="consulting-check" 
                          checked={summary.is_consulting} 
                          onChange={(e) => handleSummaryChange("is_consulting", e.target.checked)}
                          disabled={!isEditingSummary}
                        />
                        <label htmlFor="consulting-check" className="ml-2 mb-0 font-weight-bold small">Consulting</label>
                      </div>

                      {(summary.is_consulting || (!isEditingSummary && summary.consulting_firms?.length > 0)) && (
                        <div className="consulting-selection-box p-3 border rounded bg-white">
                          {isEditingSummary && (
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                              <div>
                                <button className="btn btn-xs btn-outline-secondary mr-2" onClick={handleSelectAllConsulting}>Select All</button>
                                <button className="btn btn-xs btn-outline-secondary" onClick={handleDeselectAllConsulting}>Deselect All</button>
                              </div>
                              <div className="d-flex align-items-center">
                                <input 
                                  type="text" 
                                  className="form-control form-control-sm mr-2" 
                                  placeholder="New firm name"
                                  value={newFirmName}
                                  onChange={(e) => setNewFirmName(e.target.value)}
                                  style={{ width: "200px" }}
                                />
                                <button 
                                  className="btn btn-sm btn-outline-primary" 
                                  onClick={handleAddFirm}
                                  disabled={isAddingFirm || !newFirmName.trim()}
                                >
                                  {isAddingFirm ? "Adding..." : "Add"}
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="d-flex flex-wrap" style={{ gap: "8px" }}>
                            {availableConsultingFirms.map(firm => {
                              const isSelected = summary.consulting_firms?.includes(firm);
                              if (!isEditingSummary && !isSelected) return null;
                              return (
                                <div 
                                  key={firm}
                                  className={`pill-item ${isSelected ? "selected" : ""} ${isEditingSummary ? "clickable" : ""}`}
                                  onClick={() => isEditingSummary && toggleConsultingFirm(firm)}
                                  style={{
                                    padding: "4px 12px",
                                    borderRadius: "16px",
                                    fontSize: "12px",
                                    border: "1px solid #ddd",
                                    backgroundColor: isSelected ? "#28a745" : "#f8f9fa",
                                    color: isSelected ? "#fff" : "#333",
                                    cursor: isEditingSummary ? "pointer" : "default",
                                    display: "flex",
                                    alignItems: "center"
                                  }}
                                >
                                  {firm}
                                  {isEditingSummary && isSelected && <i className="fa-solid fa-xmark ml-2" style={{ fontSize: "10px" }} />}
                                </div>
                              );
                            })}
                            {!isEditingSummary && summary.consulting_firms?.length === 0 && (
                              <span className="text-muted small">No firms selected</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-center mt-2">
                    <button className="btn base-btn" style={{ width: "250px" }} onClick={handleTriggerSearch} disabled={isSearchTriggering || isSummaryLoading || !summary || isEditingSummary}>
                      {isSearchTriggering ? "Searching..." : "Accept & Search Experts"}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-muted small text-center py-2">No summary available. Contact support or try again later.</p>
              )}
            </div>
          )}
        </div>

        <hr />

        <div className="d-flex flex-grow-1" style={{ overflow: "hidden", minHeight: "200px" }}>
          <div className="results-container flex-grow-1" style={{ overflowY: "auto", paddingRight: "15px" }}>
            {isLoading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <img src={LoadingIcon} alt="Loading..." style={{ height: "50px" }} />
                <p className="mt-2 text-muted">Searching for best matches...</p>
              </div>
            ) : (
              <>
                {visibleExperts.length > 0 ? (
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th scope="col" style={{ width: "5%" }}>#</th>
                        <th scope="col" style={{ width: "32%" }}>Name</th>
                        <th scope="col" style={{ width: "15%" }}>Score</th>
                        <th scope="col" style={{ width: "33%" }}>Match Synthesis</th>
                        <th scope="col" style={{ width: "15%" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleExperts.map((expert, index) => {
                        const isAdded = !!localAddedExperts[Number(expert.id)];
                        const scoreDetails = getScoreDetails(expert);
                        return (
                          <tr key={index}>
                            <th scope="row" className="align-middle">{index + 1}</th>
                            <td className="align-middle">
                              <div className="expert-name-container">
                                <Link
                                  to={`/app/ExpertDetails/${expert.id}`}
                                  target="_blank"
                                  className="expert-name-link font-weight-bold"
                                >
                                  {expert.name}
                                </Link>
                                {expert.position && (
                                  <div className="text-muted small mt-1" style={{ fontSize: "0.82rem", lineHeight: "1.2" }}>
                                    {expert.position}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="align-middle">
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "4px 10px",
                                  borderRadius: "12px",
                                  fontSize: "0.82rem",
                                  fontWeight: "700",
                                  backgroundColor: scoreDetails.bg,
                                  color: scoreDetails.color,
                                  border: `1px solid ${scoreDetails.border}`,
                                  whiteSpace: "nowrap",
                                  letterSpacing: "0.3px",
                                }}
                              >
                                {scoreDetails.text}
                              </span>
                            </td>
                            <td className="align-middle">
                               <div className="small" style={{ lineHeight: "1.3" }}>
                                  {expert.synthesis || "-"}
                               </div>
                            </td>

                            <td className="align-middle">
                              {isAdded ? (
                                <button 
                                  className="btn unselect-profile-btn btn-sm w-100"
                                  onClick={() => handleUnselect(expert.id)}
                                  disabled={loadingAction[expert.id]}
                                >
                                  {loadingAction[expert.id] ? "..." : "Unselect"}
                                </button>
                              ) : (
                                <button 
                                  className="btn select-profile-btn btn-sm w-100"
                                  onClick={() => handleSelect(expert.id)}
                                  disabled={loadingAction[expert.id]}
                                >
                                  {loadingAction[expert.id] ? "..." : <><i className="fa-solid fa-user-plus mr-1" /> Select</>}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center mt-5">
                    {hasSearchedEmpty ? (
                      <p className="text-muted">No experts were found matching your criteria. Try adjusting the project summary and search again.</p>
                    ) : (
                      <p className="text-muted">No search results display yet. Click "Accept & Search Experts" above.</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {results && results.length > 0 && !isLoading && (
            <div className="chat-container border-left" style={{ width: "350px", display: "flex", flexDirection: "column", paddingLeft: "15px", backgroundColor: "#fff" }}>
              <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                <span className="font-weight-bold small text-uppercase text-muted">AI Filter Chat</span>
                <button className="btn btn-xs btn-link text-danger p-0" onClick={handleClearChat} title="Clear filter">Reset</button>
              </div>

              <div className="chat-history-box flex-grow-1 mb-2" style={{ overflowY: "auto", padding: "5px" }}>
                {chatHistory.length === 0 && (
                  <div className="text-center mt-4 text-muted small">
                    <i className="fa-solid fa-comment-dots mb-2" style={{ fontSize: "1.5em" }}></i>
                    <p>Tell the AI how to refine the list.<br/><i>e.g., "Only show experts with SAP experience"</i></p>
                  </div>
                )}
                {chatHistory.map((msg, idx) => (
                  <div key={idx} className={`chat-message mb-2 p-2 rounded small ${msg.role === 'user' ? 'bg-primary text-white text-right ml-4' : msg.role === 'error' ? 'bg-danger text-white' : 'bg-light mr-4'}`}>
                    {msg.content}
                  </div>
                ))}
                {isChatLoading && (
                  <div className="chat-message mb-2 p-2 rounded small bg-light mr-4">
                    <img src={LoadingIcon} alt="thinking..." style={{ height: "15px" }} /> AI is analyzing...
                  </div>
                )}
              </div>

              <form onSubmit={handleSendChatMessage} className="chat-input-area d-flex">
                <input 
                  type="text" 
                  className="form-control form-control-sm mr-2" 
                  placeholder="Refine results..." 
                  value={chatInput} 
                  onChange={(e) => setChatInput(e.target.value)}
                  disabled={isChatLoading}
                />
                <button type="submit" className="btn btn-sm btn-primary" disabled={isChatLoading || !chatInput.trim()}>
                  <i className="fa-solid fa-paper-plane"></i>
                </button>
              </form>
            </div>
          )}
        </div>
        
        <div style={{ marginTop: "20px", textAlign: "center", paddingBottom: "10px" }}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              Close
            </button>
        </div>
      </div>
    </div>
  );
}
